import { useMemo, useState } from 'react';
import { fmtTs } from '../lib/globalTz';
import { apiFetch } from '../lib/apiClient';
import { UserReportView, type UserReportPayload } from './UserReportView';

/**
 * Entity Investigation panel - sits at the top of the Hunts tab.
 *
 * The analyst picks an entity type (IP, User, Hash, Domain, URL, Device),
 * enters the value and a lookback window, and the API runs a curated KQL set
 * across every relevant Sentinel / Defender XDR table in parallel. Results
 * come back per-table with status="ok" | "missing" (table not deployed) |
 * "error", a row count, and a sample of the rows. We render a summary strip
 * up top and one collapsible card per table below it.
 *
 * The "missing" status is important here: it lets the analyst see at a
 * glance which data sources the customer simply does not have, without us
 * pretending the hunt was a success when half the queries silently failed.
 */

type EntityType = 'ip' | 'user' | 'hash' | 'domain' | 'url' | 'device';

interface EntityOption {
  value: EntityType;
  label: string;
  placeholder: string;
  hint: string;
}

const ENTITY_OPTIONS: EntityOption[] = [
  { value: 'ip', label: 'IP Address', placeholder: '203.0.113.45', hint: 'IPv4 or IPv6' },
  { value: 'user', label: 'User / Account', placeholder: 'jane.doe@contoso.com', hint: 'UPN, email, or sAMAccountName' },
  { value: 'hash', label: 'File Hash', placeholder: 'e3b0c44298fc1c149afbf4c8996fb924…', hint: 'MD5 (32), SHA1 (40), or SHA256 (64) hex' },
  { value: 'domain', label: 'Domain', placeholder: 'malicious-c2.example.com', hint: 'FQDN (must include a dot)' },
  { value: 'url', label: 'URL', placeholder: 'https://phish.example.com/login', hint: 'Full http(s) URL' },
  { value: 'device', label: 'Device', placeholder: 'WORKSTATION-042 or DeviceId', hint: 'Hostname or Defender DeviceId' },
];

interface TableResult {
  table: string;
  description: string;
  /** Theme bucket from the backend - drives the per-domain collapsible groups
   *  (`alerts`, `signin`, `identity`, `endpoint`, `network`, `mail`, `cloud`).
   *  Optional so older API responses without the field still render. */
  group?: string;
  kql: string;
  status: 'ok' | 'missing' | 'error';
  rowCount: number;
  truncated: boolean;
  columns?: string[];
  rows?: unknown[][];
  error?: string;
}

/**
 * Stable rendering order for the per-domain collapsible sections. Anything
 * the backend returns with an unknown group falls into "Other" at the end.
 */
const GROUP_ORDER = ['alerts', 'signin', 'identity', 'endpoint', 'network', 'mail', 'cloud'] as const;
const GROUP_LABELS: Record<string, string> = {
  alerts: 'Alerts & Threat Intel',
  signin: 'Sign-in Activity',
  identity: 'Identity & Directory',
  endpoint: 'Endpoint Telemetry',
  network: 'Network & Firewall',
  mail: 'Email & URLs',
  cloud: 'Cloud Apps & O365',
  other: 'Other',
};

interface FlatTableResponse {
  kind: 'tables';
  identityResolved?: boolean;
  customerId: string;
  entityType: EntityType;
  value: string;
  days: number;
  rowCap: number;
  summary: {
    matched: number;
    empty: number;
    missing: number;
    errored: number;
    totalRows: number;
  };
  results: TableResult[];
}

interface IdentityCandidate {
  accountObjectId: string;
  accountUpn: string;
  accountName: string;
  accountDisplayName: string;
  accountSid: string;
  accountUpnSuffix: string;
}

interface AmbiguousIdentityResponse {
  kind: 'ambiguousIdentity';
  customerId: string;
  entityType: 'user';
  value: string;
  days: number;
  identityKql: string;
  candidates: IdentityCandidate[];
  message: string;
}

type InvestigationResponse = FlatTableResponse | UserReportPayload | AmbiguousIdentityResponse;

interface Props {
  customerId: string;
}

export function EntityInvestigationPanel({ customerId }: Props) {
  const [entityType, setEntityType] = useState<EntityType>('ip');
  const [value, setValue] = useState('');
  const [days, setDays] = useState(7);
  const [running, setRunning] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [report, setReport] = useState<InvestigationResponse | null>(null);
  const [expanded, setExpanded] = useState<Record<string, boolean>>({});
  // Per-group collapse state. Default = expanded; analyst can fold whole
  // domains away (e.g. collapse "Email & URLs" during a network triage).
  const [collapsedGroups, setCollapsedGroups] = useState<Record<string, boolean>>({});

  const opt = useMemo(() => ENTITY_OPTIONS.find((o) => o.value === entityType)!, [entityType]);

  async function investigate(overrideValue?: string, overrideEntityType?: EntityType) {
    // Allow explicit overrides so the disambiguation click handler can fire
    // immediately with the picked candidate's GUID instead of waiting for the
    // setState round-trip on `value`.
    const effectiveValue = (overrideValue ?? value).trim();
    const effectiveEntityType = overrideEntityType ?? entityType;
    if (!effectiveValue || running) return;
    setRunning(true);
    setError(null);
    setReport(null);
    setExpanded({});
    setCollapsedGroups({});
    try {
      const r = await apiFetch<InvestigationResponse>('/hunts/investigate', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ customerId, entityType: effectiveEntityType, value: effectiveValue, days }),
      });
      setReport(r);
      // Auto-expand any table with hits - analysts almost always want to see
      // those first, and leaving them collapsed buries the actual finding.
      // (Flat per-table flow only - the user-report flow renders all sections
      // inline and manages its own collapse state.)
      if (r.kind === 'tables') {
        // Auto-expand keys must use the same `${table}#${index}` scheme as
        // the render keys below - otherwise multiple results for the same
        // table (e.g. AuditLogs broad + AuditLogs password-reset) would
        // either collide or fail to auto-expand.
        const next: Record<string, boolean> = {};
        r.results.forEach((t, i) => {
          if (t.status === 'ok' && t.rowCount > 0) next[`${t.table}#${i}`] = true;
        });
        setExpanded(next);
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Investigation failed');
    } finally {
      setRunning(false);
    }
  }

  function downloadJson() {
    if (!report) return;
    const blob = new Blob([JSON.stringify(report, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    const safeValue = report.value.replace(/[^a-zA-Z0-9._-]/g, '_').slice(0, 60);
    a.href = url;
    a.download = `investigation_${report.entityType}_${safeValue}_${new Date().toISOString().slice(0, 10)}.json`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  }

  return (
    <div className="bg-card rounded-xl shadow-md p-4 border border-border">
      <div className="flex items-center justify-between mb-3">
        <div>
          <h2 className="text-base font-bold text-foreground">Entity Investigation</h2>
          <p className="text-xs text-muted-foreground mt-1">
            Hunt an IP, user, hash, domain, URL or device across every relevant Sentinel and Defender XDR table in one shot.
          </p>
        </div>
        <span className="text-[10px] uppercase tracking-wide font-bold px-2 py-0.5 rounded bg-primary/10 text-primary border border-primary/30">
          Auto-Hunt
        </span>
      </div>

      <div className="grid gap-2 md:grid-cols-12">
        <div className="md:col-span-3">
          <label className="block text-[11px] font-semibold text-muted-foreground uppercase tracking-wide mb-1">Entity</label>
          <select
            className="input-default w-full text-xs"
            value={entityType}
            onChange={(e) => { setEntityType(e.target.value as EntityType); setReport(null); }}
            disabled={running}
          >
            {ENTITY_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>{o.label}</option>
            ))}
          </select>
        </div>
        <div className="md:col-span-6">
          <label className="block text-[11px] font-semibold text-muted-foreground uppercase tracking-wide mb-1">Value</label>
          <input
            className="input-default w-full text-xs font-mono"
            placeholder={opt.placeholder}
            value={value}
            onChange={(e) => setValue(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') void investigate(); }}
            data-testid="input-entity-value"
            disabled={running}
          />
          <p className="text-[10px] text-muted-foreground mt-0.5">{opt.hint}</p>
        </div>
        <div className="md:col-span-1">
          <label className="block text-[11px] font-semibold text-muted-foreground uppercase tracking-wide mb-1">Days</label>
          <input
            type="number"
            min={1}
            max={90}
            className="input-default w-full text-xs"
            value={days}
            onChange={(e) => setDays(Math.min(90, Math.max(1, Number(e.target.value) || 7)))}
            disabled={running}
          />
        </div>
        <div className="md:col-span-2 flex items-end">
          <button
            onClick={() => void investigate()}
            disabled={running || !value.trim()}
            className="w-full text-xs font-semibold px-3 py-2 rounded-lg bg-primary text-white hover:bg-primary/90 disabled:opacity-50"
          >
            {running ? 'Investigating…' : 'Investigate'}
          </button>
        </div>
      </div>

      {error && (
        <div className="mt-3 px-3 py-2 bg-red-500/20 text-red-700 dark:text-red-300 text-xs rounded-lg whitespace-pre-wrap break-words">
          {error}
        </div>
      )}

      {running && !report && (
        <div className="mt-4 text-xs text-muted-foreground italic">
          Running KQL across all relevant tables in parallel. This typically takes 5 to 20 seconds depending on data volume.
        </div>
      )}

      {report && report.kind === 'userReport' && (
        <div className="mt-4">
          <UserReportView report={report} onExport={downloadJson} />
        </div>
      )}

      {report && report.kind === 'ambiguousIdentity' && (
        <div className="mt-4">
          <AmbiguousCandidatesView
            report={report}
            onPick={(candidate) => {
              // Re-run the investigation pinned to the canonical AccountObjectId
              // - this guarantees IdentityInfo collapses to exactly one row and
              // the API returns kind:userReport on the next round-trip.
              setValue(candidate.accountObjectId);
              void investigate(candidate.accountObjectId, 'user');
            }}
            onExport={downloadJson}
          />
        </div>
      )}

      {report && report.kind === 'tables' && (
        <div className="mt-4 space-y-3">
          {report.identityResolved === false && (
            <div className="px-3 py-2 rounded border border-amber-500/40 bg-amber-500/10 text-amber-700 dark:text-amber-200 text-[11px]">
              <span className="font-bold">User not found in IdentityInfo.</span>{' '}
              Falling back to a table-by-table search. To see the rich Workbook-style report, ensure
              the IdentityInfo (UEBA) data connector is enabled in the customer's Sentinel workspace.
            </div>
          )}
          <ReportSummary report={report} onExport={downloadJson} />
          {(() => {
            // Bucket results into themed groups, preserving the original index
            // so React keys / expand state stay stable across re-renders.
            // Multiple flat queries can target the same table (e.g. a generic
            // AuditLogs query and a tightly-scoped password-reset AuditLogs
            // query), so the key uses the original index, not the table name.
            const buckets = new Map<string, { result: TableResult; index: number }[]>();
            report.results.forEach((r, i) => {
              const g = r.group && GROUP_LABELS[r.group] ? r.group : 'other';
              const arr = buckets.get(g) ?? [];
              arr.push({ result: r, index: i });
              buckets.set(g, arr);
            });
            const orderedGroups: string[] = [
              ...GROUP_ORDER.filter((g) => buckets.has(g)),
              ...(buckets.has('other') ? ['other'] : []),
            ];
            return (
              <div className="space-y-3">
                {orderedGroups.map((g) => {
                  const items = buckets.get(g) ?? [];
                  const matched = items.filter((x) => x.result.status === 'ok' && x.result.rowCount > 0).length;
                  const isCollapsed = !!collapsedGroups[g];
                  return (
                    <div key={g} className="rounded-lg border border-border bg-card/40">
                      <button
                        type="button"
                        onClick={() => setCollapsedGroups((p) => ({ ...p, [g]: !p[g] }))}
                        className="w-full px-3 py-2 flex items-center gap-2 text-left hover:bg-muted/30 rounded-t-lg"
                      >
                        <span className="text-xs font-mono text-muted-foreground w-3 shrink-0">
                          {isCollapsed ? '▸' : '▾'}
                        </span>
                        <span className="text-xs font-bold uppercase tracking-wide text-foreground">
                          {GROUP_LABELS[g]}
                        </span>
                        <span className="text-[10px] text-muted-foreground">
                          {items.length} table{items.length === 1 ? '' : 's'}
                        </span>
                        {matched > 0 && (
                          <span className="text-[10px] uppercase font-bold tracking-wide px-1.5 py-0.5 rounded bg-emerald-500/15 text-emerald-700 dark:text-emerald-300 border border-emerald-500/30">
                            {matched} with hits
                          </span>
                        )}
                      </button>
                      {!isCollapsed && (
                        <div className="px-3 pb-3 space-y-2">
                          {items.map(({ result: r, index: i }) => {
                            const k = `${r.table}#${i}`;
                            return (
                              <TableResultCard
                                key={k}
                                result={r}
                                expanded={!!expanded[k]}
                                onToggle={() => setExpanded((prev) => ({ ...prev, [k]: !prev[k] }))}
                              />
                            );
                          })}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            );
          })()}
        </div>
      )}
    </div>
  );
}

function ReportSummary({
  report,
  onExport,
}: {
  report: FlatTableResponse;
  onExport: () => void;
}) {
  const { summary, value, entityType, days, rowCap } = report;
  return (
    <div className="rounded-lg border border-border bg-muted/20 p-3">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="min-w-0">
          <div className="text-xs text-muted-foreground">
            Investigation report for{' '}
            <span className="uppercase font-bold tracking-wide text-foreground">{entityType}</span>{' '}
            <span className="font-mono break-all text-foreground">{value}</span>
          </div>
          <div className="text-[11px] text-muted-foreground mt-0.5">
            Lookback: {days}d · Row cap per table: {rowCap}
          </div>
        </div>
        <button
          onClick={onExport}
          className="text-[11px] font-semibold px-2.5 py-1 rounded border border-border bg-card hover:bg-muted text-foreground shrink-0"
        >
          ↓ Export JSON
        </button>
      </div>
      <div className="mt-2 flex flex-wrap gap-2">
        <Stat label="Tables with hits" value={summary.matched} tone="hit" />
        <Stat label="Empty" value={summary.empty} tone="muted" />
        <Stat label="Not deployed" value={summary.missing} tone="warn" />
        <Stat label="Errors" value={summary.errored} tone="bad" />
        <Stat label="Total rows" value={summary.totalRows} tone="muted" />
      </div>
    </div>
  );
}

function Stat({ label, value, tone }: { label: string; value: number; tone: 'hit' | 'muted' | 'warn' | 'bad' }) {
  const cls = {
    hit: 'bg-emerald-500/15 text-emerald-700 dark:text-emerald-300 border-emerald-500/30',
    muted: 'bg-muted/40 text-muted-foreground border-border',
    warn: 'bg-amber-500/15 text-amber-700 dark:text-amber-300 border-amber-500/30',
    bad: 'bg-red-500/15 text-red-700 dark:text-red-300 border-red-500/30',
  }[tone];
  return (
    <div className={`px-2.5 py-1 rounded border text-[11px] ${cls}`}>
      <span className="font-bold">{value}</span> <span>{label}</span>
    </div>
  );
}

function TableResultCard({
  result,
  expanded,
  onToggle,
}: {
  result: TableResult;
  expanded: boolean;
  onToggle: () => void;
}) {
  const statusBadge = (() => {
    if (result.status === 'missing') {
      return <span className="text-[10px] uppercase font-bold tracking-wide px-1.5 py-0.5 rounded bg-amber-500/20 text-amber-700 dark:text-amber-300 border border-amber-500/40">Not deployed</span>;
    }
    if (result.status === 'error') {
      return <span className="text-[10px] uppercase font-bold tracking-wide px-1.5 py-0.5 rounded bg-red-500/20 text-red-700 dark:text-red-300 border border-red-500/40">Error</span>;
    }
    if (result.rowCount === 0) {
      return <span className="text-[10px] uppercase font-bold tracking-wide px-1.5 py-0.5 rounded bg-muted/40 text-muted-foreground border border-border">No matches</span>;
    }
    return (
      <span className="text-[10px] uppercase font-bold tracking-wide px-1.5 py-0.5 rounded bg-emerald-500/20 text-emerald-700 dark:text-emerald-300 border border-emerald-500/40">
        {result.truncated ? `${result.rowCount}+ matches` : `${result.rowCount} match${result.rowCount === 1 ? '' : 'es'}`}
      </span>
    );
  })();

  const interactive = result.status !== 'missing';

  return (
    <div className="rounded-lg border border-border bg-muted/10">
      <button
        type="button"
        onClick={onToggle}
        disabled={!interactive}
        className={`w-full px-3 py-2 flex items-start gap-3 text-left ${interactive ? 'hover:bg-muted/30' : 'cursor-default'}`}
      >
        <span className={`text-xs font-mono mt-0.5 w-3 shrink-0 ${interactive ? 'text-muted-foreground' : 'opacity-0'}`}>
          {expanded ? '▾' : '▸'}
        </span>
        <span className="flex-1 min-w-0">
          <span className="flex items-center gap-2 flex-wrap">
            <span className="font-mono text-xs font-bold text-foreground">{result.table}</span>
            {statusBadge}
          </span>
          <span className="block text-[11px] text-muted-foreground mt-0.5">{result.description}</span>
        </span>
      </button>

      {interactive && expanded && (
        <div className="px-3 pb-3 pt-0 border-t border-border">
          <details className="mt-2">
            <summary className="text-[10px] text-muted-foreground cursor-pointer hover:text-foreground">View KQL</summary>
            <pre className="mt-1 text-[10px] font-mono bg-background border border-border rounded p-2 overflow-x-auto whitespace-pre-wrap break-all">{result.kql}</pre>
          </details>

          {result.status === 'error' && (
            <div className="mt-2 px-2 py-1.5 bg-red-500/10 text-red-700 dark:text-red-300 text-[11px] rounded border border-red-500/30 whitespace-pre-wrap break-words font-mono">
              {result.error}
            </div>
          )}

          {result.status === 'ok' && result.rowCount > 0 && (
            <ResultsTable columns={result.columns ?? []} rows={result.rows ?? []} truncated={result.truncated} />
          )}

          {result.status === 'ok' && result.rowCount === 0 && (
            <div className="mt-2 text-[11px] text-muted-foreground italic">No matching rows in the selected window.</div>
          )}
        </div>
      )}
    </div>
  );
}

function ResultsTable({ columns, rows, truncated }: { columns: string[]; rows: unknown[][]; truncated: boolean }) {
  // Prioritise the most-useful columns first so the analyst sees timestamps
  // and key identifiers without scrolling. Hard-coded "interesting" prefix
  // list - the rest follow in source order.
  const interesting = ['TimeGenerated', 'Timestamp', 'DeviceName', 'AccountUpn', 'AccountName', 'UserPrincipalName', 'IPAddress', 'RemoteIP', 'RemoteUrl', 'FileName', 'SHA256', 'AlertName', 'Title'];
  const ordered = [
    ...interesting.filter((c) => columns.includes(c)),
    ...columns.filter((c) => !interesting.includes(c)),
  ];
  const indices = ordered.map((c) => columns.indexOf(c));

  return (
    <div className="mt-2">
      <div className="overflow-x-auto border border-border rounded">
        <table className="w-full text-[11px]">
          <thead className="bg-muted/40 sticky top-0">
            <tr>
              {ordered.map((c) => (
                <th key={c} className="text-left px-2 py-1 font-semibold text-foreground whitespace-nowrap">{c}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.slice(0, 50).map((row, i) => (
              <tr key={i} className="border-t border-border hover:bg-muted/20">
                {indices.map((idx, j) => (
                  <td key={j} className="px-2 py-1 align-top font-mono text-[10px] max-w-[280px] truncate text-foreground" title={formatCell(row[idx])}>
                    {formatCell(row[idx])}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {(rows.length > 50 || truncated) && (
        <p className="mt-1 text-[10px] text-muted-foreground italic">
          Showing first 50 rows{truncated ? ` of ${rows.length}+ (server cap reached. Narrow the window or refine the entity for a complete view.)` : rows.length > 50 ? ` of ${rows.length}` : ''}.
          Use Export JSON above to download the full result set.
        </p>
      )}
    </div>
  );
}

/**
 * Disambiguation surface - rendered when IdentityInfo returns more than one
 * candidate AccountObjectId for the analyst's input. We never silently pick
 * "the first one" because that's a foot-gun in MSSP environments where the
 * same display name or sAMAccountName can belong to multiple distinct
 * principals (think shared service-account naming conventions across tenants
 * or admin/user pairs that share a UPN prefix).
 *
 * Clicking a candidate re-runs investigate() pinned to that candidate's
 * AccountObjectId GUID, which collapses IdentityInfo to exactly one row and
 * gets the analyst into the rich Workbook-style report on the next round-trip.
 */
function AmbiguousCandidatesView({
  report,
  onPick,
  onExport,
}: {
  report: AmbiguousIdentityResponse;
  onPick: (candidate: IdentityCandidate) => void;
  onExport: () => void;
}) {
  return (
    <div className="space-y-3">
      <div className="rounded-lg border border-amber-500/40 bg-amber-500/10 p-3">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div className="min-w-0">
            <div className="text-xs font-bold text-amber-700 dark:text-amber-200 uppercase tracking-wide">
              Multiple identities matched
            </div>
            <div className="text-[11px] text-amber-100/90 mt-1">
              {report.message}
            </div>
            <div className="text-[10px] text-amber-100/70 mt-1">
              Input:{' '}
              <span className="font-mono break-all text-amber-50">{report.value}</span>
              {' · '}Lookback: 90d (identity resolution window)
            </div>
          </div>
          <button
            onClick={onExport}
            className="text-[11px] font-semibold px-2.5 py-1 rounded border border-amber-500/40 bg-amber-500/10 hover:bg-amber-500/20 text-amber-100 shrink-0"
          >
            ↓ Export JSON
          </button>
        </div>
      </div>

      <div className="text-[11px] text-muted-foreground">
        Pick the principal you want to investigate. The report will reload pinned to its AccountObjectId.
      </div>

      <ul className="space-y-2" data-testid="list-identity-candidates">
        {report.candidates.map((c) => (
          <li key={c.accountObjectId}>
            <button
              type="button"
              onClick={() => onPick(c)}
              className="w-full text-left rounded-lg border border-border bg-muted/10 hover:bg-muted/30 hover:border-primary/50 px-3 py-2 transition-colors group"
              data-testid={`button-pick-candidate-${c.accountObjectId}`}
            >
              <div className="flex items-start justify-between gap-3 flex-wrap">
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-sm font-bold text-foreground truncate">
                      {c.accountDisplayName || c.accountName || c.accountUpn || c.accountObjectId}
                    </span>
                    {c.accountUpnSuffix && (
                      <span className="text-[10px] uppercase font-bold tracking-wide px-1.5 py-0.5 rounded bg-primary/10 text-primary border border-primary/30">
                        @{c.accountUpnSuffix}
                      </span>
                    )}
                  </div>
                  <dl className="mt-1.5 grid gap-x-4 gap-y-0.5 text-[11px] grid-cols-1 sm:grid-cols-[auto,1fr]">
                    {c.accountUpn && (
                      <>
                        <dt className="text-muted-foreground">UPN</dt>
                        <dd className="font-mono text-foreground break-all">{c.accountUpn}</dd>
                      </>
                    )}
                    {c.accountName && (
                      <>
                        <dt className="text-muted-foreground">sAMAccountName</dt>
                        <dd className="font-mono text-foreground break-all">{c.accountName}</dd>
                      </>
                    )}
                    <dt className="text-muted-foreground">AccountObjectId</dt>
                    <dd className="font-mono text-foreground break-all">{c.accountObjectId}</dd>
                    {c.accountSid && (
                      <>
                        <dt className="text-muted-foreground">SID</dt>
                        <dd className="font-mono text-foreground break-all">{c.accountSid}</dd>
                      </>
                    )}
                  </dl>
                </div>
                <span className="text-[11px] font-semibold text-primary group-hover:translate-x-0.5 transition-transform shrink-0 self-center">
                  Investigate →
                </span>
              </div>
            </button>
          </li>
        ))}
      </ul>

      <details>
        <summary className="text-[10px] text-muted-foreground cursor-pointer hover:text-foreground">
          View identity-resolution KQL
        </summary>
        <pre className="mt-1 text-[10px] font-mono bg-background border border-border rounded p-2 overflow-x-auto whitespace-pre-wrap break-all">{report.identityKql}</pre>
      </details>
    </div>
  );
}

const ISO_DATETIME_RE = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(\.\d+)?Z?$/;

function formatCell(v: unknown): string {
  if (v === null || v === undefined) return '';
  if (typeof v === 'string') {
    if (ISO_DATETIME_RE.test(v)) return fmtTs(v);
    return v;
  }
  if (typeof v === 'number' || typeof v === 'boolean') return String(v);
  try {
    return JSON.stringify(v);
  } catch {
    return String(v);
  }
}
